import type { ApiResponse } from '@shared/types'
import { toApiResponse } from '@shared/utils'
import { toast } from 'sonner'
import type { EvalProgressUpdate, EvalStreamRequest } from 'v2/pages/flow/components/eval/types'
import type { RagMcpSource } from 'v2/pages/flow/knowledge-base/rag-types'
import type {
  McpArticle,
  RKnowledgeBase,
  SyncDocumentPayload,
  SyncSourcePayload,
} from 'v2/pages/flow/knowledge-base/types'
import type { SecretVariable } from 'v2/pages/flow/sidebar/types'
import type { FlowService, VibeFeedbackResponse } from 'v2/pages/flow/types'
import {
  baseService,
  configService,
  coreService,
  globalHodorService,
  globalHodorServiceV2,
  runnerService,
} from 'v2/shared/services/base-services'
import {
  consoleAddDocumentToRag,
  consoleCreateToRag,
  consoleUpdateToRag,
  ragCrawlStatusToConsole,
  ragKbToConsole,
  ragMcpArticleToConsole,
  ragMcpCollectionToConsole,
  statusTreeIfPresent,
} from 'v2/shared/services/rag-adapter'
import {
  ragAddSources,
  ragCancelCrawl,
  ragCreateKb,
  ragDeleteDoc,
  ragDeleteKb,
  ragDeleteSource,
  ragFetchCollectionArticles,
  ragFetchDocFromSource,
  ragFetchDocsByFetchId,
  ragFetchDocsFromSource,
  ragGetCrawlStatus,
  ragGetKb,
  ragGetKbStatus,
  ragListKbs,
  ragResyncKb,
  ragResyncSource,
  ragRetryDoc,
  ragStartCrawl,
  ragUpdateDoc,
  ragUpdateKb,
} from 'v2/shared/services/rag-kb-service'

// hodor rewrites /v1/ai-assist/* to /v1/* before forwarding to aiassist.
const AI_ASST_BASE = '/v1/ai-assist'
const SENTINEL_BASE = '/v1/sentinel'
const VOICE_BASE = `${AI_ASST_BASE}/voice-ai-config`
const VIBE_AGENT_BASE = `${AI_ASST_BASE}/vibe-agent`
const VOICE_CLONING_BASE = `${SENTINEL_BASE}/voice-cloning`

export const FLOW_ENDPOINTS = {
  phlo: (id?: string) => `/phlo${id ? `/${id}` : ''}`,
  phloPaginated: '/flow?paginated=true',
  flowRuns: '/flow/run',
  flowRunsPaginated: '/flow/run?paginated=true',
  flowRunDetails: (phloRunUuid: string) => `/flow/run/${phloRunUuid}/logs`,
  initializePlayground: (phloID: string) => `/phlo/${phloID}/playground`,
  phloVersions: (phloID: string) => `/phlo/${phloID}/versions`,
  switchPhloVersion: (phloID: string) => `/phlo/${phloID}/switch-version`,
  phloVersionSummary: (phloID: string) => `/phlo/${phloID}/version-summary`,
  phloMetaStatus: (phloID: string) => `/phlo/${phloID}/meta-status`,
  phloNodeMeta: (phloID: string, nodeID: string) => `/phlo/${phloID}/node/${nodeID}/meta`,
  triggerApiRequest: (authId: string, flowId: string) => `/account/${authId}/flow/${flowId}`,
  triggerStartFlowRunner: (authId: string, phloId: string) => `/account/${authId}/phlo/${phloId}`,
  executeHttp: '/exec_http',
  updateFlowSettings: (id: string) => `/phlo/${id}/settings`,
  generateFunctionSchema: `${AI_ASST_BASE}/aiassistant/generate-function-schema`,
  generateHttpFunctionSchema: `${AI_ASST_BASE}/aiassistant/generate-http-function-schema`,
  mapActionParameters: `${AI_ASST_BASE}/aiassistant/map-action-parameters`,
  generateCode: `${AI_ASST_BASE}/aiassistant/generate-code`,
  generateIntentName: `${AI_ASST_BASE}/aiassistant/generate-intent-name`,
  generateNodeMetadata: `${AI_ASST_BASE}/aiassistant/generate-node-metadata`,
  // agent-runner (hodor /v1 -> runner): infers an Input's collect type + options from the node text.
  suggestCollectionConfiguration: 'collection-configuration/suggest',
  voices: (language: string, currentVoiceId?: string) =>
    `${SENTINEL_BASE}/voice-config/voices?language=${language}${
      currentVoiceId ? `&current_voice_id=${encodeURIComponent(currentVoiceId)}` : ''
    }`,
  textToSpeech: `${VOICE_BASE}/text-to-speech`,
  languages: `${VOICE_BASE}/languages`,
  providers: (languages?: string) =>
    `${VOICE_BASE}/providers${languages ? `?language=${languages}` : ''}`,
  cancelWorkflowStream: `${VIBE_AGENT_BASE}/cancel`,
  vibeAgentFeedback: (sessionId: string) => `${VIBE_AGENT_BASE}/sessions/${sessionId}/feedback`,
  resetVibeConversation: (phloId: string) => `${VIBE_AGENT_BASE}/conversations/${phloId}/reset`,
  vibeAgentConversation: (phloUuid: string) => `${VIBE_AGENT_BASE}/conversations/${phloUuid}`,
  secretVariables: `${AI_ASST_BASE}/variables`,
  secretVariable: (id: string) => `${AI_ASST_BASE}/variables/${id}`,
  slackChannels: '/slack/channels',
  slackCreateApp: '/slack/apps/create-link',
  generateGoals: (id: string) => `/phlo/${id}/generate-goals`,
  startEval: `${AI_ASST_BASE}/eval/start`,
  cancelEval: `${AI_ASST_BASE}/eval/cancel`,
  simulationScenarios: `${AI_ASST_BASE}/eval/simulation/scenarios`,
  generateSimulationScenarios: `${AI_ASST_BASE}/eval/simulation/scenarios/generate`,
  runSimulationScenarios: `${AI_ASST_BASE}/eval/simulation/scenarios/run`,
  batchDeleteSimulationScenarios: `${AI_ASST_BASE}/eval/simulation/scenarios/batch-delete`,
  simulationRuns: `${AI_ASST_BASE}/eval/simulation/runs`,
  simulationRun: (runUuid: string) => `${AI_ASST_BASE}/eval/simulation/runs/${runUuid}`,
  simulationRunScenario: (runUuid: string, scenarioUuid: string) =>
    `${AI_ASST_BASE}/eval/simulation/runs/${runUuid}/scenarios/${scenarioUuid}`,
  renameSimulationRun: (runUuid: string) =>
    `${AI_ASST_BASE}/eval/simulation/runs/${runUuid}/rename`,
  exportSimulationRun: (runUuid: string) =>
    `${AI_ASST_BASE}/eval/simulation/runs/${runUuid}/export`,
  voiceCloning: `${VOICE_CLONING_BASE}/voices`,
  communityVoices: `${VOICE_CLONING_BASE}/voices/community`,
} as const

// The KB↔agent-flow link ("Associated Agent Flows") is owned by PHLO-core, not rag-service — the
// console sources/sets it via the config-service reverse endpoints. Best-effort by design: a link
// read/write must never fail the KB list/detail/create it decorates.
//
// SER-5976 (uuid cutover): these reverse endpoints are keyed on the KB **uuid** — the same stable
// external id the picker offers, that flow-save stores in a flow's `knowledge_base_ids`, and that KB
// detail deep-links by. The config-service `/knowledge-base-phlos` reverse-read takes `{ kb_ids: [uuid] }`
// and returns `{ uuid: [phlo_uuid] }`; PHLO-core stores `knowledge_base_ids` as uuids. rag no longer
// returns the sequential integer kb_id on any KB response, so nothing here can (or does) key on it.
const ragGetPhloUuids = async (kbIds: string[]): Promise<Record<string, string[]> | null> => {
  if (!kbIds.length) return {}
  try {
    // contacto-core wraps proxied responses in a { data } envelope ({ api_id, data, errors, ... }),
    // so unwrap .data to get the { uuid: [phlo_uuid] } map (fall back to the body if ever bare).
    const resp = (await configService.post('/knowledge-base-phlos', { kb_ids: kbIds })) as
      | Record<string, string[]>
      | { data?: Record<string, string[]> }
    return (
      (resp as { data?: Record<string, string[]> })?.data ??
      (resp as Record<string, string[]>) ??
      {}
    )
  } catch {
    // Return null (NOT {}) so callers can tell a FAILED read from a genuinely-empty one. An empty map
    // looks authoritative: a destructive full-replace of an agent's knowledge_base_ids derived from it
    // would silently drop existing links on a transient failure. Display callers coalesce null → [];
    // link editors flag the KBs `linksUnavailable` and refuse the full-replace.
    return null
  }
}

// Propagates on failure (does NOT swallow): the caller decides how to surface it — createKnowledgeBase
// keeps the just-created KB and warns; updateKnowledgeBase warns without failing the whole edit. A
// silent void-resolve here made those callers' warning paths dead code.
const ragSetPhloUuids = async (kbId: string, phloUuids: string[]): Promise<void> => {
  await configService.post(`/knowledge-base/${kbId}/phlos`, { phlo_uuids: phloUuids })
}

/**
 * Creates an instance of the AuthService.
 * @returns An object with methods for authentication.
 */
const serviceFactory = (): FlowService => ({
  getProviders: (languages) => baseService.get(FLOW_ENDPOINTS.providers(languages)),
  addDocument: async (data, id) => {
    const updated = await ragAddSources(id, consoleAddDocumentToRag(data))
    // Associated agents live in PHLO-core; resolve them so the returned KB doesn't transiently
    // show 0 agents (passing [] dropped the associations until the next refetch). Keyed on the KB uuid.
    const phloMap = await ragGetPhloUuids([updated.uuid])
    return toApiResponse({
      data: {
        ...ragKbToConsole(updated, null, phloMap?.[updated.uuid] ?? []),
        linksUnavailable: phloMap === null,
      },
    })
  },
  getFlowConfig: (phloID, phloVersion) => {
    const endpoint =
      phloVersion && phloVersion !== 'latest'
        ? `${FLOW_ENDPOINTS.phlo(phloID as string)}?phlo_version=${phloVersion}`
        : FLOW_ENDPOINTS.phlo(phloID as string)
    return configService.get(endpoint)
  },
  upsertFlow: (data, id = '') =>
    id
      ? configService.post(FLOW_ENDPOINTS.phlo(id as string), data)
      : configService.post(FLOW_ENDPOINTS.phlo(), data),
  updateFlowSettings: (data, id) => configService.post(FLOW_ENDPOINTS.updateFlowSettings(id), data),
  deleteFlow: (id) => configService.delete(FLOW_ENDPOINTS.phlo(id as string)),
  execHttpRequest: (data) => configService.post(FLOW_ENDPOINTS.executeHttp, data),
  generateFunctionSchema: (data) => baseService.post(FLOW_ENDPOINTS.generateFunctionSchema, data),
  generateHttpFunctionSchema: (data) =>
    baseService.post(FLOW_ENDPOINTS.generateHttpFunctionSchema, data),
  mapActionParameters: (data) => baseService.post(FLOW_ENDPOINTS.mapActionParameters, data),
  generateCode: (data) => baseService.post(FLOW_ENDPOINTS.generateCode, data),
  generateIntentName: (
    instruction: string,
    existingIntents?: Array<{
      intent_name: string
      intent_instructions: string
    }>,
  ) =>
    baseService.post(FLOW_ENDPOINTS.generateIntentName, {
      instruction,
      existing_intents: existingIntents || [],
    }),
  generateNodeMetadata: (data) =>
    baseService.post(FLOW_ENDPOINTS.generateNodeMetadata, {
      node_type: data.nodeType,
      config: data.config,
      existing_nodes: data.existingNodes || [],
      existing_name: data.existingName,
      existing_description: data.existingDescription,
    }),
  generateGoals: (
    id: string,
    conversationGoals: Array<{ goal_name: string; goal_instructions: string }>,
  ) =>
    configService.post(FLOW_ENDPOINTS.generateGoals(id), { conversation_goals: conversationGoals }),
  postVibeFeedback: (sessionId, body) =>
    baseService.post<ApiResponse<VibeFeedbackResponse>>(
      FLOW_ENDPOINTS.vibeAgentFeedback(sessionId),
      body,
    ),
  cancelWorkflowStream: (body: { session_id: string; response_id: string }) =>
    baseService.post<ApiResponse<{ cancelled: boolean }>>(
      FLOW_ENDPOINTS.cancelWorkflowStream,
      body,
    ),
  startEvalStream: (body, handlers) =>
    baseService.stream<EvalStreamRequest, EvalProgressUpdate>(
      FLOW_ENDPOINTS.startEval,
      body,
      handlers,
    ),
  cancelEval: (evalId: string) => baseService.post(FLOW_ENDPOINTS.cancelEval, { eval_id: evalId }),
  listSimulationScenarios: (phloUuid: string, page = 1, pageSize = 50) =>
    baseService.get(
      `${FLOW_ENDPOINTS.simulationScenarios}?phlo_uuid=${phloUuid}&page=${page}&page_size=${pageSize}`,
    ),
  generateSimulationScenarios: (
    body: Record<string, unknown>,
    handlers: Parameters<typeof baseService.stream>[2],
  ) => baseService.stream(FLOW_ENDPOINTS.generateSimulationScenarios, body, handlers),
  deleteSimulationScenario: (uuid: string) =>
    baseService.delete(`${FLOW_ENDPOINTS.simulationScenarios}/${uuid}`),
  deleteAllSimulationScenarios: (phloUuid: string) =>
    baseService.delete(`${FLOW_ENDPOINTS.simulationScenarios}?phlo_uuid=${phloUuid}`),
  batchDeleteSimulationScenarios: (uuids: string[]) =>
    baseService.post(FLOW_ENDPOINTS.batchDeleteSimulationScenarios, { uuids }),
  runSelectedScenariosStream: (
    body: Record<string, unknown>,
    handlers: Parameters<typeof baseService.stream>[2],
  ) => baseService.stream(FLOW_ENDPOINTS.runSimulationScenarios, body, handlers),
  listSimulationRuns: (phloUuid: string, page = 1, pageSize = 20) =>
    baseService.get(
      `${FLOW_ENDPOINTS.simulationRuns}?phlo_uuid=${phloUuid}&page=${page}&page_size=${pageSize}`,
    ),
  getSimulationRun: (runUuid: string) => baseService.get(FLOW_ENDPOINTS.simulationRun(runUuid)),
  getSimulationRunScenario: (runUuid: string, scenarioUuid: string) =>
    baseService.get(FLOW_ENDPOINTS.simulationRunScenario(runUuid, scenarioUuid)),
  renameSimulationRun: (runUuid: string, name: string) =>
    baseService.patch(FLOW_ENDPOINTS.renameSimulationRun(runUuid), { name }),
  exportSimulationRun: (runUuid: string) =>
    baseService.get(FLOW_ENDPOINTS.exportSimulationRun(runUuid)),
  listKnowledgeBases: async () => {
    const kbs = await ragListKbs()
    const phloMap = await ragGetPhloUuids(kbs.map((k) => k.uuid))
    // phloMap === null ⇒ the reverse-read failed; flag each KB so link editors (popover / agent KB
    // tab) don't full-replace knowledge_base_ids from an un-authoritative set. Decorate with [] either
    // way so the list display (agent count) is unaffected. Keyed on the KB uuid.
    const data = kbs.map((k) => ({
      ...ragKbToConsole(k, null, phloMap?.[k.uuid] ?? []),
      linksUnavailable: phloMap === null,
    }))
    return toApiResponse({ data, totalCount: data.length })
  },
  crawlSite: async (data) => {
    const r = await ragStartCrawl({
      url: data.url,
      max_pages: data.max_pages,
      max_depth: data.max_depth,
      crawl_subpages: data.crawl_subpages,
      include_paths: data.include_paths?.length ? data.include_paths : null,
      exclude_paths: data.exclude_paths?.length ? data.exclude_paths : null,
    })
    return toApiResponse({ data: { job_id: r.job_id } })
  },
  getCrawlSiteStatus: async (jobId) => {
    const r = await ragGetCrawlStatus(jobId as string)
    return toApiResponse({ data: ragCrawlStatusToConsole(r) })
  },
  cancelCrawl: async (jobId) => {
    const r = await ragCancelCrawl(jobId as string)
    // rag returns `cancelled` as a stringified bool ("true"/"false"); the console contract is boolean.
    return toApiResponse({ data: { cancelled: String(r.cancelled).toLowerCase() === 'true' } })
  },
  getDocumentsFromSource: async (source: string, sourceId: string, articles = false) => {
    // articles=false (default for the picker): SPACES-ONLY tree — the worker returns collections with
    // no pages, so a 11k-page Confluence renders in seconds; each space's pages load on expand.
    const r = await ragFetchDocsFromSource(source as RagMcpSource, sourceId, articles)
    return toApiResponse({ data: { fetch_id: r.fetch_id } })
  },
  getCollectionArticles: async (source: string, collectionId: string, sourceId: string) => {
    // Lazy expand: one collection's (e.g. Confluence space's) pages, fetched on demand and CAPPED by
    // rag (the picker shows ~50; select the whole space for the rest). Map rag-native articles → the
    // console McpArticle shape; carry has_more so the picker can show a "more available" footer.
    const r = await ragFetchCollectionArticles(source as RagMcpSource, collectionId, sourceId)
    return toApiResponse({
      data: { articles: (r.articles || []).map(ragMcpArticleToConsole), hasMore: !!r.has_more },
    })
  },
  getDocumentsFromSourceByFetchId: async (fetchId) => {
    const r = await ragFetchDocsByFetchId(fetchId)
    // rag returns the tree root as a single collection (or [] before it's built); the console picker
    // expects an array of root nodes. Map rag-native nodes → console McpArticleCollection.
    const roots = Array.isArray(r.data) ? r.data : [r.data]
    const data = roots.map(ragMcpCollectionToConsole)
    return toApiResponse({ data: { status: r.status, data } })
  },
  getDocumentFromSource: async (source, sourceId, documentId) => {
    const article = await ragFetchDocFromSource(source as RagMcpSource, documentId, sourceId)
    // rag 404s resolve to null; the console contract is non-null McpArticle, so map when present and
    // pass null through (React Query data is nullable at the call site).
    return toApiResponse({
      data: (article ? ragMcpArticleToConsole(article) : null) as McpArticle,
    })
  },
  createKnowledgeBase: async (data) => {
    const { body, phloUuids } = consoleCreateToRag(data)
    const created = await ragCreateKb(body)
    // The KB now EXISTS. Linking it to the agent flow(s) lives in PHLO-core and is best-effort: if it
    // fails, do NOT reject the create (that would orphan the just-created KB and lead the user to
    // retry → a duplicate). Warn instead, so they know the agent association didn't take and can re-link.
    if (phloUuids.length) {
      try {
        // PHLO-core keys the KB↔flow reverse link on the KB uuid (see ragGetPhloUuids) — the same
        // stable external id the picker/list/detail use. The write is keyed on `created.uuid`.
        await ragSetPhloUuids(created.uuid, phloUuids)
      } catch {
        toast.warning(
          'Knowledge base created, but linking it to the agent failed. Open the agent and re-add it.',
        )
      }
    }
    // SER-5976: hand back the uuid so the just-created KB is linked into the agent's `knowledge_base_ids`
    // and deep-linked by the same stable external id the picker/list/detail now use.
    return toApiResponse({ data: { knowledge_base_id: created.uuid } })
  },
  deleteKnowledgeBase: async (id) => {
    await ragDeleteKb(id as string)
    return toApiResponse({ data: undefined as unknown as void })
  },
  updateKnowledgeBase: async (data, id) => {
    const updated = await ragUpdateKb(id, consoleUpdateToRag(data))
    // associated agent flows ("Associated Agent Flows") live in PHLO-core, not rag. The link write is
    // best-effort: warn (don't fail the whole edit) if it doesn't take, so the KB update still returns.
    if (data.phlo_uuids !== undefined) {
      try {
        await ragSetPhloUuids(id, data.phlo_uuids)
      } catch {
        toast.warning(
          'Knowledge base updated, but linking it to the agent(s) failed. Open the agent and re-link.',
        )
      }
    }
    // Resolve the CURRENT associations from PHLO-core. A partial patch (e.g. an enable/disable
    // toggle) doesn't send phlo_uuids, so returning `data.phlo_uuids || []` would transiently show
    // 0 agents until the next refetch. After ragSetPhloUuids this also reflects the just-set value.
    const phloMap = await ragGetPhloUuids([updated.uuid])
    return toApiResponse({
      data: {
        ...ragKbToConsole(updated, null, phloMap?.[updated.uuid] ?? []),
        linksUnavailable: phloMap === null,
      },
    })
  },
  getKnowledgeBase: async (id) => {
    // rag GET /kb/{id} is configured to resolve (not throw) on 404 → a body with no uuid; map that
    // to {data:null} so the detail hook's null-check still works.
    const summary = await ragGetKb(id as string)
    if (!summary?.uuid) return toApiResponse({ data: null as unknown as RKnowledgeBase })
    // The status tree and the phlo-link read are independent once we know the KB exists, so fetch them
    // in parallel (this runs on a 5s poll — serial awaits were 3 round-trips/tick; now 1 + a parallel
    // pair). ragGetKbStatus resolves (not throws) on 404 → a body with NO uuid. Treat that as "no
    // tree" so the mapper falls back to the summary's lifecycle status instead of reading undefined
    // fields off the error body (mapRollupStatus(undefined) → undefined embedding_status crashed the poller).
    const [treeResp, phloMap] = await Promise.all([
      // both legs catch → null so one leg failing can't reject the pair and lose the other's result.
      // (ragGetPhloUuids already returns null on error; the explicit .catch keeps the legs symmetric.)
      ragGetKbStatus(id as string).catch(() => null),
      ragGetPhloUuids([summary.uuid]).catch(() => null),
    ])
    // Status-tree existence marker: SER-5976 (backend #256) made GET /kb/{id}/status uuid-only — the
    // body now carries `uuid`, not the sequential integer `kb_id`. statusTreeIfPresent returns the tree
    // only when that `uuid` is present (a real 200) and null for a 404-resolved empty body — falling
    // back to the summary's lifecycle status. Colocated with ragKbToConsole (its consumer) + unit-tested.
    const tree = statusTreeIfPresent(treeResp)
    return toApiResponse({
      data: {
        ...ragKbToConsole(summary, tree, phloMap?.[summary.uuid] ?? []),
        linksUnavailable: phloMap === null,
      },
    })
  },
  syncKnowledgeBase: async (id) => {
    await ragResyncKb(id as string)
    // Echo back the uuid the caller passed (the KB just resynced) rather than the resync ack's integer
    // kb_id — the console is uuid-clean, and every KB reference it hands out is the uuid.
    return toApiResponse({ data: { id: id as string } })
  },
  syncKnowledgeBaseDocument: async (data: SyncDocumentPayload) => {
    // re-ingest one document = rag's per-document retry (kb-scoped)
    await ragRetryDoc(data.kbId, data.documentId)
    return toApiResponse({ data: undefined as unknown as void })
  },
  syncKnowledgeBaseSource: async (data: SyncSourcePayload) => {
    await ragResyncSource(data.kbId, data.sourceId)
    return toApiResponse({ data: undefined as unknown as void })
  },
  deleteKnowledgeBaseSource: async (data) => {
    await ragDeleteSource(data?.kbId || '', data?.sourceId || '')
    return toApiResponse({ data: undefined as unknown as void })
  },
  deleteDocument: async (data) => {
    await ragDeleteDoc(data?.kbId || '', data?.documentId || '')
    return toApiResponse({ data: undefined as unknown as void })
  },
  updateDocument: async (data, documentId, kbId) => {
    await ragUpdateDoc(kbId, documentId, { enabled: !!data.enabled })
    return toApiResponse({ data: undefined as unknown as RKnowledgeBase })
  },
  getVoices: (language: string, currentVoiceId?: string) =>
    baseService.get(FLOW_ENDPOINTS.voices(language, currentVoiceId)),
  textToSpeech: (data) => baseService.post(FLOW_ENDPOINTS.textToSpeech, data),
  getLanguages: () => baseService.get(FLOW_ENDPOINTS.languages),
  listSecretVariables: async () => {
    const resp = await baseService.get<SecretVariable[]>(FLOW_ENDPOINTS.secretVariables)
    return toApiResponse({ data: resp, totalCount: resp.length })
  },
  createSecretVariable: (payload) => baseService.post(FLOW_ENDPOINTS.secretVariables, payload),
  updateSecretVariable: (id, payload) =>
    baseService.patch(FLOW_ENDPOINTS.secretVariable(id), payload),
  deleteSecretVariable: (id) => baseService.delete(FLOW_ENDPOINTS.secretVariable(id as string)),
  initializePlayground: (id, body = {}) =>
    coreService.post(FLOW_ENDPOINTS.initializePlayground(id), body),
  getPhloMetaStatus: (id: string) => configService.get(FLOW_ENDPOINTS.phloMetaStatus(id)),
  getPhloNodeMeta: (phloId: string, nodeId: string) =>
    configService.get(FLOW_ENDPOINTS.phloNodeMeta(phloId, nodeId)),
  // Legacy (migrated) flows run on cx-flow-runner — same v1/v2 split as the
  // message trigger: Hodor /v2 proxies to cx-flow-runner, /v1 to the legacy runner.
  suggestCollectionConfiguration: (payload) =>
    runnerService.post(FLOW_ENDPOINTS.suggestCollectionConfiguration, payload),
  triggerApiRequest: (authId, flowId, queryParams, body, isLegacy) =>
    (isLegacy ? globalHodorServiceV2 : globalHodorService).post(
      FLOW_ENDPOINTS.triggerApiRequest(authId, flowId),
      body,
      queryParams,
    ),
  // Legacy (migrated) phlos run on cx-flow-runner — playground SMS starts them
  // via Hodor's /v2 proxy instead of queue-execution-service.
  triggerStartFlowRunner: (authId, phloId, queryParams, body) =>
    globalHodorServiceV2.post(
      FLOW_ENDPOINTS.triggerStartFlowRunner(authId, phloId),
      body,
      queryParams,
    ),
  listFlowsPaginated: (params) => configService.get(FLOW_ENDPOINTS.phloPaginated, params),
  listVersions: (phloID) => configService.get(FLOW_ENDPOINTS.phloVersions(phloID)),
  switchVersion: (phloID, sourceVersionId) =>
    configService.post(FLOW_ENDPOINTS.switchPhloVersion(phloID), {
      source_version_id: sourceVersionId,
    }),
  editVersionSummary: (phloID, versionId, summary, description) =>
    configService.post(FLOW_ENDPOINTS.phloVersionSummary(phloID), {
      version_id: versionId,
      change_summary: summary,
      // Key-presence contract: an omitted change_description leaves the stored value
      // untouched (a title-only edit can't wipe an AI description that landed mid-edit).
      ...(description !== undefined && { change_description: description }),
    }),
  getSlackChannels: (payload) => configService.post(FLOW_ENDPOINTS.slackChannels, payload),
  getSlackCreateAppLink: () => configService.get(FLOW_ENDPOINTS.slackCreateApp),
  resetVibeConversation: (data) =>
    baseService.post(FLOW_ENDPOINTS.resetVibeConversation(data.phlo_uuid), data),
  getVibeAgentConversation: (phloUuid: string) =>
    baseService.get(FLOW_ENDPOINTS.vibeAgentConversation(phloUuid)),
  listFlowRunsPaginated: (params) => configService.get(FLOW_ENDPOINTS.flowRunsPaginated, params),
  listFlowRuns: (params) => configService.get(FLOW_ENDPOINTS.flowRuns, params),
  getFlowRunDetails: (phloRunUuid: string, evalSource?: 'ao') =>
    configService.get(
      FLOW_ENDPOINTS.flowRunDetails(phloRunUuid),
      evalSource ? { eval_source: evalSource } : undefined,
    ),
  // Voice Cloning
  cloneVoice: (formData: FormData) =>
    baseService.post(
      FLOW_ENDPOINTS.voiceCloning,
      formData,
      {},
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    ),
  getCommunityVoices: (params) => baseService.get(FLOW_ENDPOINTS.communityVoices, params),
})

/**
 * A service instance for the authentication API or the Hodor Service (Global)
 * @returns Available methods for authentication.
 */
export const flowService = serviceFactory()
